import { groupAccessIdFromGroupId, studentAccessIdFromUserId } from "../../../shared/access-id";
import { WorksheetType } from "../../../shared/types/worksheet";
import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY } from "../components/AISettings";
import { AI_WORKSHEET_CONTEXT_KEY } from "../constants";
import { getOrFetchDocData } from "../context/doc-context";

import { docToPageAndAnswerContext } from "./doc-query";

import { serverGetAISchemaResponse } from "api/api-server-ai";
import {
  AIModelType,
  AISchemaId,
  AIServerFileType,
  AIServerMessageType,
  SubmissionType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { RootState, store } from "store/index";
import {
  pageSelectors,
  selectContextMessages,
  selectCurrentSectionId,
  selectEnabledSectionUsers,
  selectNestedPageOutcomes,
} from "store/selectors";
import { rollbarAndLogError } from "utils/logger";
import { isGroupPage } from "utils/page-utils";

/**
 * This prompt sets the general system prompt context for AI assessment on a specific page
 * outcome + student submission combination. It explains section outcomes, page outcomes, how
 * they fit together, and how we want assessments to look like on a certain page outcome + student
 * submission combination.
 */
export const ASSESSMENT_SYSTEM_PROMPT = [
  "The user is currently in assessment mode, trying to assess an individual student or group's submissions",
  "to in-class exercises and assignments.",
  "Your main concern is to assess based on learning outcomes, and give helpful but concise feedback.",
  "A course outcome is a learning objective that a course is designed to teach.",
  // TODO: This is duplicate with some of the page context
  'Up to three course outcomes may be linked to a specific "Page" (which is either an assignment,',
  "a breakout activity, or a short in-class activity), and a Page should always be assessed on outcomes linked to it,",
  "unless there are no linked outcomes, in which case formative feedback should be given solely based on how closely the",
  "student answers follow the instructions on the Page.",
  // TODO: This is duplicate with some of the outcome context
  "Each course outcome has a specific rubric that may be overwritten at the page level for additional detail.",
  "When you assess a student's submission, you should consider how well the student's work aligns",
  "with the specific outcomes and rubrics, and avoid giving scores based on other factors.",
  "Focus on giving formative feedback for how the student could specifically improve.",
  "Feedback should be written addressing the student as 'you', and should be as concise as possible.",
  "If a submission is lacking information from the student, you should still assess the submission and give appropriately low scores.",
  "For code written by students, also give feedback on the quality of the code, and whether it is human readable and easy to understand,",
  "though make sure to primarily assess on the outcomes rubric: the code quality, errors, and bugs are secondary concerns.",
].join("\n");

const noFilesPrompt = [
  "Note that you're unable to access any files uploaded by students, and you should not count them towards the score.",
  "If it looks like a submission is substantially consisting of uploaded files, you should refuse to assess the submission and let them know you cannot read uploaded files yet.",
].join("\n");

export const selectAccessIdFromSubmission = (
  state: RootState,
  submission: SubmissionType,
): string | null => {
  const page = pageSelectors.selectById(state, submission.page_id);
  const isGrouped = isGroupPage(page);
  if (isGrouped) {
    return submission.page_group_id ? groupAccessIdFromGroupId(submission.page_group_id) : null;
  }
  const userId = selectEnabledSectionUsers(state).find(
    (cu) => cu.id === submission.section_user_id,
  )?.user_id;
  if (userId) {
    return studentAccessIdFromUserId(userId);
  }
  throw new Error("Could not find user ID for submission");
};

export const selectSubmissionAssessmentContext = (
  state: RootState,
  pageId: string,
  docData: WorksheetType,
  accessId: string,
): { prompt: AIServerMessageType[]; files: AIServerFileType[] } => {
  // We might be making the request against a different page than the current one
  // in order to load assessments in the background, so we create our own docContext
  // rather than use the worksheet context.
  const { context: docAndAnswerContext, files } = docToPageAndAnswerContext(docData, pageId, [
    { name: "", accessId },
  ]);

  // TODO: This is a slight hack - preferably we'd give the worksheets IDs and make it so the AI
  // can have multiple worksheets in context at once, deciding itself which one to use. That's
  // more error/bug prone for now, so likely won't happen until much later.
  const contextMessages = selectContextMessages(state).filter(
    (message) => message.id !== AI_WORKSHEET_CONTEXT_KEY,
  );

  const prompt: AIServerMessageType[] = [
    {
      role: "system",
      content: [files.length ? "" : noFilesPrompt].join("\n"),
    },
    ...contextMessages.map((m) => ({ role: m.role, content: m.text })),
    {
      role: "system",
      content: docAndAnswerContext,
    },
    {
      role: "user",
      content: ["Please help me with assessing the current student submission."].join("\n\n"),
    },
  ];

  return { prompt, files };
};

export type AIAssessmentResponseType = {
  outcomes: { pageOutcomeId: string; score: number }[];
  feedback: string;
  refused?: string;
  errorMessage?: string;
};

export const serverAssessSubmission = async (
  submission: SubmissionType,
  model: AIModelType = DEFAULT_MODEL_FOR_ASSESSMENT_QUERY,
): Promise<AIAssessmentResponseType> => {
  try {
    const state = store.getState();
    const sectionId = selectCurrentSectionId(state);
    const page = pageSelectors.selectById(state, submission.page_id);
    const accessId = selectAccessIdFromSubmission(state, submission);
    if (!page || accessId == null) {
      return {
        outcomes: [],
        feedback: "",
        errorMessage: t("assessment_card.ai.error"),
      };
    }
    const pageOutcomes = selectNestedPageOutcomes(state, page.id);

    const docData = await getOrFetchDocData(
      page.worksheet_id,
      submission.worksheet_sharedb_version,
    );
    const { prompt, files } = selectSubmissionAssessmentContext(state, page.id, docData, accessId);

    const response = await serverGetAISchemaResponse({
      section_id: sectionId,
      model,
      prompt,
      files,
      schemaId: AISchemaId.ASSESSMENT,
    }).catch((e: Error) => e);

    if (response instanceof Error) {
      rollbarAndLogError("serverAssessSubmission: server error.", response);
      return {
        outcomes: [],
        feedback: "",
        errorMessage: t("assessment_card.ai.error"),
      };
    }

    if (response.refused) {
      return {
        outcomes: [],
        feedback: "",
        refused: t("assessment_card.ai.refused", { reason: response.refused }),
      };
    }

    const outcomes = Object.keys(response.outcomes)
      .map((outcomeName) => {
        // We tell the AI to refer to outcomes with a # prefix, but internally we represent them
        // without the prefix, so we ensure no prefix here. We don't allow special characters in
        // outcome titles, so this is safe.
        const outcomeTitle = outcomeName.replaceAll("#", "");
        const pageOutcome = pageOutcomes.find((po) => po.outcome.title === outcomeTitle);
        if (!pageOutcome) {
          // If the AI invents an outcome, we ignore it
          return null;
        }
        return {
          pageOutcomeId: pageOutcome.id,
          score: Number(response.outcomes[outcomeName]),
        };
      })
      .filter((o): o is { pageOutcomeId: string; score: number } => o != null);

    return {
      outcomes,
      feedback: response.feedback ?? "",
    };
  } catch (error) {
    rollbarAndLogError("serverAssessSubmission: unknown error.", error);
    return {
      outcomes: [],
      feedback: "",
      errorMessage: t("assessment_card.ai.error"),
    };
  }
};
